import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Link2, Plus, Copy, Pencil, Trash2, Power, Loader2 } from 'lucide-react';

export default function MultiLocationLinks() {
    const navigate = useNavigate();
    const [links, setLinks] = useState(null);

    const load = async () => {
        try {
            const r = await api.get('/multi-location');
            setLinks(r.data.links || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load');
            setLinks([]);
        }
    };
    useEffect(() => { load(); }, []);

    const masterUrl = (slug) => `${window.location.origin}/m/${slug}`;

    const toggle = async (l) => {
        const next = l.status === 'active' ? 'inactive' : 'active';
        try {
            await api.patch(`/multi-location/${l.id}`, { status: next });
            toast.success(next === 'active' ? 'Published' : 'Deactivated');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    };
    const duplicate = async (l) => {
        try { await api.post(`/multi-location/${l.id}/duplicate`); toast.success('Duplicated'); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    };
    const remove = async (l) => {
        if (!window.confirm(`Delete "${l.name}"? Entry cards will be detached.`)) return;
        try { await api.delete(`/multi-location/${l.id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    };

    if (links === null) {
        return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
    }

    return (
        <div className="max-w-5xl mx-auto p-6" data-testid="mll-list-page">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-sky-500/10"><Link2 className="w-6 h-6 text-sky-500" /></div>
                    <div>
                        <h1 className="text-2xl font-bold">Multi-Location Links</h1>
                        <p className="text-sm text-slate-500">One master link → visitor selects a location → existing card experience.</p>
                    </div>
                </div>
                <Button data-testid="mll-create-btn" onClick={() => navigate('/multi-location-links/new')} className="bg-sky-600 hover:bg-sky-500 text-white">
                    <Plus className="w-4 h-4 mr-1" />Create
                </Button>
            </div>

            {links.length === 0 ? (
                <div className="text-center py-16 text-slate-500 border border-dashed rounded-2xl" data-testid="mll-empty">
                    No Multi-Location Links yet. Create your first one.
                </div>
            ) : (
                <div className="space-y-3" data-testid="mll-links">
                    {links.map((l) => (
                        <div key={l.id} data-testid={`mll-row-${l.id}`} className="flex flex-wrap items-center gap-3 p-4 rounded-2xl border border-slate-200 dark:border-slate-800">
                            <div className="flex-1 min-w-[200px]">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold">{l.name}</span>
                                    <Badge variant={l.status === 'active' ? 'default' : 'outline'}
                                        className={l.status === 'active' ? 'bg-emerald-600' : ''} data-testid={`mll-status-${l.id}`}>
                                        {l.status}
                                    </Badge>
                                </div>
                                <button onClick={() => { navigator.clipboard.writeText(masterUrl(l.slug)); toast.success('Master link copied'); }}
                                    className="text-xs text-sky-600 hover:underline flex items-center gap-1 mt-1" data-testid={`mll-copy-${l.id}`}>
                                    <Copy className="w-3 h-3" />{masterUrl(l.slug)}
                                </button>
                                <p className="text-xs text-slate-500 mt-1">{l.location_count} locations · {l.entry_card_count} entry cards</p>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Button size="sm" variant="outline" data-testid={`mll-edit-${l.id}`} onClick={() => navigate(`/multi-location-links/${l.id}`)}><Pencil className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" data-testid={`mll-toggle-${l.id}`} onClick={() => toggle(l)}><Power className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" data-testid={`mll-duplicate-${l.id}`} onClick={() => duplicate(l)}><Copy className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" data-testid={`mll-delete-${l.id}`} onClick={() => remove(l)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
